import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CellContext } from '@tanstack/react-table';

import { useErrorContext } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContext';
import { WebEDIError } from 'services/webediErrors/types';
import { classNames } from 'support/helpers/generic/generic';
import { WebEDIRowData } from 'components/DataTable/Types';
import { useFieldState } from 'components/SchemableForm/hooks/useFieldState';
import { useValue } from 'components/WebEDI/helpers';

import { CellProps } from '../../types';
import {
  disableArrowKeys,
  isProcurosAddedValue,
  useCellClassNames,
  useIsReadonly,
  useNumberConfigurations,
} from '../cellHelpers';
import { RowStatus } from '../components/RowStatus/RowStatus';
import { InputInsideCell } from './InputInsideCell';
import { InputWrapperCell } from './InputWrapperCell';

export type Props = CellContext<WebEDIRowData, unknown> & CellProps;

export const NumberCell = ({ fieldId, column, row, table, isHighlighted, mode }: Props) => {
  const { t } = useTranslation();
  const {
    columnDef: { meta: columnMeta, ...columnDef },
    getIsFirstColumn,
    getIsLastColumn,
  } = column;

  const inputValue = useValue(row.original, fieldId, columnMeta?.optionalConfig?.expression);
  const { service } = useErrorContext<WebEDIError>();
  const error = service.useErrorById(fieldId);
  const { prefix, formattedValue, suffix } = useNumberConfigurations({
    value: inputValue,
    mode,
    meta: columnMeta,
    rowData: row.original,
    fieldId,
  });

  const { register } = useFormContext();
  const isSubmitting = table.options.meta?.isSubmitting ?? false;
  const isAdded = isProcurosAddedValue(row);
  const isReadOnly = useIsReadonly({ isAdded, mode, columnMeta, rowIndex: row.index, fieldId });
  const disabled = isSubmitting;
  const fieldState = useFieldState(fieldId, error);
  const cellClassNames = useCellClassNames({
    isLastColumn: getIsLastColumn(),
    isFirstColumn: getIsFirstColumn(),
    row,
    table,
  });

  const inputClassNames = classNames(
    {
      'group-hover:bg-red-100 bg-red-50 focus:!bg-transparent disabled:!bg-red-50': fieldState === 'error',
      'group-hover:bg-blue-100 bg-blue-50 focus:!bg-transparent disabled:!bg-blue-50 text-blue-700 focus:text-inherit focus-visible:!ring-blue-500 focus-visible:!border-blue-500':
        fieldState === 'pending-validation',
      'group-hover:bg-procuros-green-50 focus:!bg-transparent disabled:!bg-transparent': fieldState === 'default',
    },
    cellClassNames,
    'text-end',
    columnMeta?.optionalConfig?.isStatusColumn ? '!pl-10' : undefined,
  );

  return (
    <InputWrapperCell
      fieldId={fieldId}
      isHighlighted={isHighlighted}
      className={cellClassNames}
      meta={columnMeta}
      fieldState={fieldState}
    >
      <InputInsideCell
        className={classNames(
          'h-full bg-transparent size-full !border-none p-3 !outline-none text-xs !shadow-none rounded-none min-w-[140px]',
          inputClassNames,
        )}
        placeholder={!formattedValue && error?.type === 'required' ? t('common:table.errors.requiredField') : undefined}
        title={formattedValue ? String(formattedValue) : undefined}
        readOnly={isReadOnly}
        disabled={disabled}
        type="number"
        {...register(fieldId, { valueAsNumber: true })}
        value={columnMeta?.optionalConfig?.expression ? inputValue : undefined}
        fieldState={fieldState}
        leftIcon={
          columnMeta?.optionalConfig?.isStatusColumn ? (
            <RowStatus
              statusColumn={columnMeta?.optionalConfig?.quantityField}
              currentPath={fieldId}
              isProcurosAdded={isAdded}
              currentColumn={columnDef && 'accessorKey' in columnDef ? (columnDef.accessorKey as string) : ''}
              tooltips={{
                deleted: t('webedi:inputCell.status.deleted'),
                partial: t('webedi:inputCell.status.partial'),
                complete: t('webedi:inputCell.status.completed'),
              }}
            />
          ) : null
        }
        rightAddon={suffix}
        leftAddon={prefix}
        step={0.01}
        onKeyDown={disableArrowKeys}
        id={fieldId}
        data-field={fieldId}
        data-testid={fieldId}
        data-1p-ignore
      />
    </InputWrapperCell>
  );
};
